import {cache} from "../../lib/utils/storage";
import {hasCookie} from "../../lib/utils/cookies";

type FollowingRecord = {
    channels: number[],
    fighters: number[]
}

type FollowActionResponse = {
    status: number,
    message?: string
}

type FollowingResponse = {
    status: number,
    data?: FollowingRecord,
    message?: string
}


function isFollowing(selector: string, id: number, followed: FollowingRecord){
    if (selector === 'channel'){
        return followed.channels.includes(id)
    }else if(selector === 'fighter'){
        return followed.fighters.includes(id)
    }
    return false
}

class Follow {
    selector: string
    following
    stylesActive = ["bg-mono-white", "text-mono-ns-300", "hover:bg-mono-ps-100"]
    stylesInactive = ["bg-mono-ns-200", "text-mono-white", "hover:bg-mono-ns-300"]
    cacheKey = "fite:followed"

    constructor() {
        this.selector = "[data-js-follow]";
        this.following = cache(this.cacheKey) as FollowingRecord;
        this.init()
    }

    init(): void {
        const elements = document.querySelectorAll<HTMLButtonElement>(this.selector)

        if (elements.length === 0)
            return

        if (hasCookie('eid', document.cookie)){
            if (!this.following){
                this.fetchFollowed()
                    .then((data: FollowingRecord | null) => {
                        if (data){
                            this.following = data;
                            this.renderFollowButtons(elements, data);
                        }
                    })
                    .catch((err) => {
                        console.error(err)
                    })
            }else{
                this.renderFollowButtons(elements, this.following as FollowingRecord)
            }
        }else{
            elements.forEach((element) => {
                this.renderFollowButton(element, false)
                element.addEventListener('click', this.handleAnonymousClick)
            })
        }
    }

    handleAnonymousClick(ev: MouseEvent){
        ev.preventDefault()
        if (window.confirm('Please sign in to follow organizations and fighters.')) {
            window.location.href = '/account/signin/?return_to=' + encodeURIComponent(window.location.href);
        }
    }

    handleFollowButton(ev: MouseEvent){
        const allowed = ["channel", "fighter"];
        const target = ev.target as HTMLElement;
        let el: HTMLButtonElement;
        if (!target)
            return;

        if (target.tagName === "BUTTON"){
            el = target as HTMLButtonElement
        }else if(target.parentNode && (target.parentNode as HTMLElement).tagName === 'BUTTON'){
            el = target.parentNode as HTMLButtonElement
        }else{
            return;
        }

        const data = el.dataset

        if (el.disabled){
            return;
        }
        el.disabled = true

        if (!data.jsFollow || !allowed.includes(data.jsFollow) || !data.id){
            return
        }

        let url = '/account/follow/';
        let unfollow_message;
        const formData: FormData = new FormData();
        formData.append("id", data.id)

        if (data.jsFollow === 'channel'){
            formData.append("t", "c");
            unfollow_message = 'Do you want to unfollow this channel?';
        }else if(data.jsFollow === 'fighter'){
            formData.append("t", "f");
            unfollow_message = 'Do you want to unfollow this fighter?';
        }

        if (data.active){
            if (!window.confirm(unfollow_message)){
                el.disabled = false
                return;
            }
            url = '/account/unfollow/'
        }

        fetch(url, {
            method: 'POST',
            cache: 'no-cache',
            credentials: "include",
            body: formData
        })
            .then((response) => response.json())
            .then((result: FollowActionResponse) => {
                if (!data.jsFollow || !data.id){
                    return
                }
                if (!data.active){
                    this._handleFollowResponse(result, el, data.jsFollow, parseInt(data.id))
                }else{
                    this._handleUnFollowResponse(result, el, data.jsFollow, parseInt(data.id))
                }
            })
            .catch(() => {
                el.disabled = false
            })
    }

    _handleFollowResponse(data: FollowActionResponse, element: HTMLButtonElement, selector: string, objectId: number){
        if (![200, 409].includes(data.status)){
            element.disabled = false
            return
        }
        this.renderFollowButton(element, true)
        element.disabled = false
        if (window.dataLayer)
            window.dataLayer.push({'event': 'follow'});

        if (selector === 'channel' && !this.following.channels.includes(objectId)){
            this.following.channels.push(objectId)
            cache(this.cacheKey, this.following, 15)
        }else if (selector === 'fighter' && !this.following.fighters.includes(objectId)){
            this.following.fighters.push(objectId)
            cache(this.cacheKey, this.following, 15)
        }
        document.querySelector<HTMLElement>("body")?.dispatchEvent(new CustomEvent(
            'notify',
            {
                bubbles: true,
                detail: { message: "Followed!", type: "success"}
            }))
    }

    _handleUnFollowResponse(data: FollowActionResponse, element: HTMLButtonElement,  selector: string, objectId: number){
        if (![200, 409].includes(data.status)){
            element.disabled = false
            return
        }
        this.renderFollowButton(element, false)
        element.disabled = false
        if (window.dataLayer)
            window.dataLayer.push({'event': 'unfollow'});

        if (selector === 'channel' && this.following.channels.includes(objectId)){
            this.following.channels.splice(this.following.channels.indexOf(objectId), 1)
            cache(this.cacheKey, this.following, 15)
        }else if (selector === 'fighter' && this.following.fighters.includes(objectId)){
            this.following.fighters.splice(this.following.fighters.indexOf(objectId), 1)
            cache(this.cacheKey, this.following, 15)
        }

        document.querySelector<HTMLElement>("body")?.dispatchEvent(new CustomEvent(
            'notify',
            {
                bubbles: true,
                detail: { message: "Unfollowed!", type: "success"}
            }))
    }

    renderFollowButton(element: HTMLButtonElement, isFollowing: boolean){
        const span = element.querySelector<HTMLSpanElement>('span')
        const svg = element.querySelector<SVGUseElement>('svg use')
        if (!span || !svg)
            return;
        if (isFollowing){
            element.dataset["active"] = 'true'
            span.textContent = 'Following'
            element.classList.remove(...this.stylesInactive)
            element.classList.add(...this.stylesActive)
            svg.setAttribute('xlink:href', "#icon-check");
        }else{
            delete element.dataset["active"]
            span.textContent = 'Follow'
            element.classList.remove(...this.stylesActive)
            element.classList.add(...this.stylesInactive)
            svg.setAttribute('xlink:href', "#icon-plus");
        }
        element.classList.remove('hidden');
    }
    renderFollowButtons(elements: NodeListOf<HTMLButtonElement>, following: FollowingRecord){
        const allowed = ["channel", "fighter"];

        elements.forEach((element) => {
            const data = element.dataset

            if (!data.jsFollow || !allowed.includes(data.jsFollow) || !data.id){
                // hide button ?
                return
            }
            this.renderFollowButton(
                element,
                isFollowing(data.jsFollow, parseInt(data.id), following)
            )

            element.addEventListener('click', this.handleFollowButton.bind(this))
        })
    }

    fetchFollowed(){
        return fetch('/account/following/')
            .then((response) => response.json())
            .then((data: FollowingResponse) => {
                if (data.status === 200){
                    cache('fite:followed', data.data, 60)
                    return data.data || null
                }
                return null
            }).catch((err) => {
                console.error(err)
                return null;
            })
    }
}


export default Follow
